import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiBookOpen, FiTarget, FiMap, FiEdit3, FiDownload, FiArrowRight, FiStar, FiHeart } = FiIcons;

const WelcomeOnboarding = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [authorData, setAuthorData] = useState({
    name: '',
    experience: '',
    writingGoal: '',
    preferredTime: ''
  });

  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to Your Writing Calling!',
      subtitle: 'God has given you a message to share with the world',
      icon: FiHeart,
      color: 'from-sage-green to-soft-blue'
    },
    {
      id: 'profile',
      title: 'Tell Us About Yourself',
      subtitle: 'Help us personalize your writing journey',
      icon: FiUser,
      color: 'from-soft-blue to-sage-green'
    },
    {
      id: 'tour',
      title: 'Your 5-Step Writing Process',
      subtitle: 'Discover how to plan your book with purpose',
      icon: FiMap,
      color: 'from-sage-green to-warm-brown'
    },
    {
      id: 'ready',
      title: 'You\'re Ready to Begin!',
      subtitle: 'Your writing journey starts now',
      icon: FiStar,
      color: 'from-warm-brown to-sage-green'
    }
  ];

  const tourSteps = [
    { icon: FiTarget, title: 'Book Vision', desc: 'Define your purpose and calling' },
    { icon: FiMap, title: 'Structure & Outline', desc: 'Build your book\'s framework' },
    { icon: FiBookOpen, title: 'Project Scope', desc: 'Set realistic goals and timelines' },
    { icon: FiEdit3, title: 'Chapter Kickstart', desc: 'Write your powerful opening' },
    { icon: FiDownload, title: 'Summary & Export', desc: 'Get your complete book plan' }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const completeOnboarding = () => {
    const onboardingData = {
      authorName: authorData.name, // Store the user's name properly
      experience: authorData.experience,
      writingGoal: authorData.writingGoal,
      preferredTime: authorData.preferredTime,
      completedAt: new Date().toISOString(),
      hasSeenOnboarding: true
    };
    
    localStorage.setItem('planningWithPurpose_onboarding', JSON.stringify(onboardingData));
    onComplete(onboardingData);
  };

  const currentStepData = steps[currentStep];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Progress Bar */}
        <div className="p-6 pb-0">
          <div className="flex justify-between items-center mb-4">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  index <= currentStep
                    ? 'bg-sage-green text-white'
                    : 'bg-gray-200 text-gray-400'
                }`}
              >
                {index + 1}
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className="bg-gradient-to-r from-sage-green to-soft-blue h-2 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="p-8"
          >
            {/* Step Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                className={`bg-gradient-to-r ${currentStepData.color} rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4`}
              >
                <SafeIcon icon={currentStepData.icon} className="text-2xl text-white" />
              </motion.div>
              <h2 className="font-serif text-2xl text-warm-brown mb-2">
                {currentStepData.title}
              </h2>
              <p className="text-warm-brown/70">
                {currentStepData.subtitle}
              </p>
            </div>

            {/* Step Content */}
            {currentStep === 0 && (
              <div className="text-center space-y-6">
                <div className="bg-sage-green/10 rounded-lg p-6">
                  <p className="text-warm-brown/80 italic mb-2">
                    "Before I formed you in the womb I knew you, before you were born I set you apart; I appointed you as a prophet to the nations."
                  </p>
                  <p className="text-sm text-warm-brown/60">- Jeremiah 1:5</p>
                </div>
                <p className="text-warm-brown">
                  Your book isn't just a project—it's a calling. Let's help you steward this gift with clarity and confidence.
                </p>
              </div>
            )}

            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-warm-brown font-medium mb-2">
                    What should we call you?
                  </label>
                  <input
                    type="text"
                    value={authorData.name}
                    onChange={(e) => setAuthorData({...authorData, name: e.target.value})}
                    placeholder="Your name"
                    className="w-full px-4 py-3 rounded-xl border border-warm-brown/20 focus:border-sage-green focus:ring-2 focus:ring-sage-green/20 outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-warm-brown font-medium mb-2">
                    What's your writing experience?
                  </label>
                  <select
                    value={authorData.experience}
                    onChange={(e) => setAuthorData({...authorData, experience: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-warm-brown/20 focus:border-sage-green focus:ring-2 focus:ring-sage-green/20 outline-none"
                  >
                    <option value="">Select your experience level</option>
                    <option value="first-time">This is my first book</option>
                    <option value="some-writing">I've done some writing before</option>
                    <option value="published-author">I'm a published author</option>
                    <option value="experienced">I've written multiple books</option>
                  </select>
                </div>

                <div>
                  <label className="block text-warm-brown font-medium mb-2">
                    What's your main goal for this book?
                  </label>
                  <textarea
                    value={authorData.writingGoal}
                    onChange={(e) => setAuthorData({...authorData, writingGoal: e.target.value})}
                    placeholder="e.g., To help new believers grow in their faith..."
                    rows="3"
                    className="w-full px-4 py-3 rounded-xl border border-warm-brown/20 focus:border-sage-green focus:ring-2 focus:ring-sage-green/20 outline-none resize-none"
                  />
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                <p className="text-center text-warm-brown mb-8">
                  Our 5-step process will guide you from idea to complete book plan:
                </p>
                <div className="grid gap-4">
                  {tourSteps.map((step, index) => (
                    <motion.div
                      key={step.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center p-4 bg-gradient-to-r from-cream to-soft-blue/10 rounded-xl"
                    >
                      <div className="bg-sage-green/20 rounded-lg p-3 mr-4">
                        <SafeIcon icon={step.icon} className="text-sage-green text-xl" />
                      </div>
                      <div>
                        <h3 className="font-medium text-warm-brown">{step.title}</h3>
                        <p className="text-sm text-warm-brown/70">{step.desc}</p>
                      </div>
                      <div className="ml-auto text-2xl text-warm-brown/30 font-serif">
                        {index + 1}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="text-center space-y-6">
                <div className="bg-gradient-to-r from-sage-green/10 to-soft-blue/10 rounded-lg p-6">
                  <SafeIcon icon={FiStar} className="text-3xl text-sage-green mx-auto mb-4" />
                  <h3 className="font-medium text-warm-brown mb-2">
                    {authorData.name ? `You're equipped and ready, ${authorData.name}!` : "You're equipped and ready!"}
                  </h3>
                  <p className="text-warm-brown/70">
                    Trust in the Lord's guidance as you begin this writing journey. Your story matters.
                  </p>
                </div>
                <div className="bg-warm-brown/5 rounded-lg p-4">
                  <p className="text-sm text-warm-brown/80 italic">
                    "Commit to the Lord whatever you do, and he will establish your plans."
                  </p>
                  <p className="text-xs text-warm-brown/60 mt-1">- Proverbs 16:3</p>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="p-6 pt-0 flex justify-between">
          <div>
            {currentStep > 0 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="text-warm-brown/60 hover:text-warm-brown transition-colors"
              >
                Back
              </button>
            )}
          </div>
          <motion.button
            onClick={handleNext}
            disabled={currentStep === 1 && (!authorData.name || !authorData.experience)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="bg-gradient-to-r from-sage-green to-soft-blue text-white px-6 py-2 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {currentStep === steps.length - 1 ? 'Begin Writing!' : 'Continue'}
            <SafeIcon icon={FiArrowRight} className="ml-2" />
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default WelcomeOnboarding;